import type { Scenario } from '../../src/engine/schema.js';

/**
 * Two scenarios, and the tool asks the reader to choose between them: does
 * Pine Island get built as proposed, or not.
 *
 * Every scenario states its counterfactual. The schema requires it, because
 * "not built" is meaningless until you write down what happens instead, and
 * every number in that column inherits the answer.
 *
 * v1 carried a third scenario, `delayed`, modelling the restraining order in
 * force since 22 May 2026. v2 removes it: the question here is what Minnesota
 * gets or loses, and a timing shift answers a different question. The court
 * record still appears in the assumptions, it just no longer drives a column.
 */
export const scenarios: Scenario[] = [
  {
    id: 'built',
    label: 'Built as proposed',
    plainLabel: 'It gets built',
    summary:
      'The project proceeds on the approvals the city granted in January and February 2026, on the schedule the developer proposed.',
    counterfactual:
      'Construction begins on the 482-acre site in Goodhue County, and the taxes, payments and construction work described here are collected and worked in Minnesota.',
    inputOverrides: {},
    sourceIds: ['src_city_skyway', 'src_city_subsidy'],
  },
  {
    id: 'not_built',
    label: 'Not built',
    plainLabel: 'It does not get built',
    summary:
      'The project does not go ahead in Pine Island. The land stays in agricultural use and Minnesota collects none of the taxes, payments or construction work.',
    // The load-bearing sentence in the whole pack. "Not built" does not mean
    // the demand evaporates, and every emissions figure in this column inherits
    // that. Stated generically: a state with a more carbon-intensive grid, with
    // Missouri named as the worked example because that is the grid the
    // emissions metrics actually compare against. The reasoning for choosing
    // Missouri rather than a neighbouring state is in narrative.onEmissions.
    counterfactual:
      'The same compute is built and served somewhere else, in a state whose grid is more carbon-intensive than Minnesota’s. Missouri is the example used here, because its grid region is roughly half coal. Minnesota gets none of the taxes, jobs or payments; the emissions still happen, and are higher.',
    inputOverrides: {},
    sourceIds: ['src_egrid'],
    // The weaker-sourced column by construction: no public reporting names an
    // alternative site for THIS project. Flagged so the UI marks it.
    lowConfidence: true,
  },
];
